import { inflect } from 'inflection'
import { match } from 'ts-pattern'
import {
  Text,
  Primary13Regular,
  RedRed60,
  Totals,
  Box,
  Flex
} from '@sequencehq/core-components'
import type { Currency } from '@sequencehq/api'
import { AppliedCreditNote } from 'Invoices/types'
import { CreditNoteLineItemRow } from './CreditNoteLineItemTable'
import { CreditedInvoice, getCreditNoteTotals } from './creditNoteTotals'
import {
  CREDIT_NOTE_EXCEEDS_CREDITABLE_MESSAGE,
  CREDIT_NOTE_EXCEEDS_INVOICE_MESSAGE,
  CREDIT_NOTE_NEGATIVE_TOTAL_MESSAGE
} from './issueCreditNote.constants'

type Props = {
  rows: CreditNoteLineItemRow[]
  currency: Currency
  invoice: CreditedInvoice
  // A refund-manually credit note isn't applied to the invoice, so the
  // invoice-impact rows and the creditable-ceiling validation are hidden.
  appliesToInvoice: boolean
  // Total already paid to the invoice, itemised alongside credit notes so the
  // creditable figures reconcile against the original amount. The creditable
  // cap already accounts for payments (via the ledger balance); this is display.
  previouslyPaid?: number
}

export const TotalsSection = ({
  rows,
  currency,
  invoice,
  appliesToInvoice,
  previouslyPaid = 0
}: Props) => {
  const {
    subtotal,
    tax,
    total,
    original,
    previouslyCredited,
    remainingCreditable,
    adjusted,
    exceedsCreditable,
    isNegativeCredit
  } = getCreditNoteTotals(rows, invoice)

  const hasAppliedCreditNotes = invoice.appliedCreditNotes.length > 0

  // A negative total is rejected at finalisation whether or not the credit is
  // applied to the invoice, so unlike the creditable ceiling this message isn't
  // gated on appliesToInvoice.
  const validation = match({
    isNegativeCredit,
    showsCeiling: appliesToInvoice && exceedsCreditable
  })
    .with({ isNegativeCredit: true }, () => ({
      message: CREDIT_NOTE_NEGATIVE_TOTAL_MESSAGE,
      testId: 'issue-credit-note.validation.negative-total'
    }))
    .with({ showsCeiling: true }, () => ({
      // With prior credits the cap is what remains creditable, so the message
      // references the running total across credit notes.
      message: hasAppliedCreditNotes
        ? CREDIT_NOTE_EXCEEDS_CREDITABLE_MESSAGE
        : CREDIT_NOTE_EXCEEDS_INVOICE_MESSAGE,
      testId: 'issue-credit-note.validation.exceeds-invoice'
    }))
    .otherwise(() => undefined)

  return (
    <Flex flexDir="column" alignItems="flex-end" gap="8px">
      <Box width="320px">
        <Totals currency={currency}>
          <Totals.Row title="Subtotal" amount={subtotal} />
          {/* Tax is shown as a single line: rows may carry differing tax rates,
              so there is no single percentage to display via Totals.Tax. */}
          <Totals.Row title="Tax" amount={tax} />
          <Totals.Total title="Total amount to credit" amount={total} />
          {appliesToInvoice && (
            <>
              <Totals.Divider />
              <Totals.Row title="Original invoice amount" amount={original} />
              {hasAppliedCreditNotes && (
                <Totals.Row
                  title={previouslyCreditedTitle(invoice.appliedCreditNotes)}
                  amount={-previouslyCredited}
                />
              )}
              {previouslyPaid > 0 && (
                <Totals.Row title="Previously paid" amount={-previouslyPaid} />
              )}
              {/* Remaining creditable floors at zero; the over-credit signal is
                  carried by the adjusted amount going negative and red below. */}
              <Totals.Row
                title="Remaining creditable"
                amount={remainingCreditable}
              />
              <Totals.Total
                title="Adjusted invoice amount due"
                amount={adjusted}
                color={exceedsCreditable ? RedRed60 : undefined}
              />
            </>
          )}
        </Totals>
      </Box>
      {validation && (
        <Box width="320px">
          <Text
            {...Primary13Regular}
            color={RedRed60}
            data-testid={validation.testId}
          >
            {validation.message}
          </Text>
        </Box>
      )}
    </Flex>
  )
}

const previouslyCreditedTitle = (
  appliedCreditNotes: AppliedCreditNote[]
): string => {
  const single =
    appliedCreditNotes.length === 1 ? appliedCreditNotes.at(0) : undefined

  if (single) {
    return `Previously credited (${single.creditNoteNumber ?? '1 credit note'})`
  }

  return `Previously credited (${appliedCreditNotes.length} ${inflect(
    'credit note',
    appliedCreditNotes.length
  )})`
}
